import { useState } from "react";
import Link from "next/link";
import Layout from "../../components/layout";
import Head from "next/head";
import { userForgotPass } from "../api/api.js";
import { useRouter } from "next/router";
import { Loading_ellipsis } from "../../components/loading";
import CloseIcon from "../../public/editor/close_cross.svg";
import style from "./password.module.scss";
import Image from "next/image";
import getForgotPasswordLocales from "../../locales/locales_forgot_password";
import getErrorLocales from "../../locales/locales_error";
import getCommonLocales from "../../locales/locales_common";
import MainBlob from "../../components/animate/MainBlob";
import OpenGraphTwitterCard from "../../components/seo/OpenGraphTwitterCard";

export default function ForgotPassword() {
  const router = useRouter();

  const { locale, pathname } = router;

  const dictionary = {
    ...getCommonLocales(locale),
    ...getForgotPasswordLocales(locale),
    ...getErrorLocales(locale),
  };

  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [error, setError] = useState("");
  const [formDisabled, setFormDisabled] = useState(false);

  const formValid = () => {
    let condition = /^([a-zA-Z0-9_.-])+@([a-zA-Z0-9_.-])+\.([a-zA-Z])+([a-zA-Z])+/;
    if (email.length > 0 && !condition.test(email)) {
      setEmailError(dictionary["Invalid e-mail address"]);
      return false;
    } else if (email.length === 0) {
      setEmailError(dictionary["Required field to fill in"]);
      return false;
    } else {
      return true;
    }
  };

  const formSubmit = async (event) => {
    event.preventDefault();
    setFormDisabled(true);
    if (formValid()) {
      let data = { email: email.toLowerCase(), locale: locale, };
      let response = await userForgotPass(data);
      if (199 < response.status && response.status < 300) {
        setStep(1);
      } else {
        setError(dictionary[response.data.message] ? dictionary[response.data.message] : response.data.message);
      }
    }
    setFormDisabled(false);
  };

  const getCanonicalLink = (locale) => {
    const enList = ["en", "ja", "ar"];

    if (enList.includes(locale)) {
      return `https://bgrem.ai/forgot-password/`;
    } else {
      return `https://bgrem.ai/${locale}/forgot-password/`;
    }
  };

  return (
    <Layout mainClassName="auth">
      <Head>
        <title>{dictionary["title"]}</title>
        <meta name="description" content={dictionary["description"]} />
      </Head>
      <OpenGraphTwitterCard title={dictionary["title"]} description={dictionary["description"]} url={pathname} canonical={getCanonicalLink(locale)} />
      <div className={style.window}>
        {step === 0 ? (
          <>
            <h1 className={style.title} dangerouslySetInnerHTML={{ __html: `${dictionary["page title"]}` }} />
            <form className={style.form} onSubmit={formSubmit}>
              <Link className={style.close} href="/sign-in">
                <CloseIcon />
              </Link>
              {error.length ? (
                <div className={style["request-error"]}>
                  <Image width="15" height="15" src="/ico/error.png" alt="" />
                  {error}
                </div>
              ) : (
                ""
              )}
              <div className={style["form-text"]}>
                {dictionary["Please enter the e-mail address you provided during registration. We will send you a personalized link to change your password."]}
              </div>
              <div className={style["form-item"]}>
                <label htmlFor="e-mail" className={`${style.label} ${emailError.length !== 0 ? style.error : ""}`}>
                  Email
                </label>
                <input className={`${style.input} ${emailError.length !== 0 ? style.error : ""}`} id="e-mail" name="e-mail" type="text"
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setEmailError("")}
                  onBlur={formValid}
                  value={email}
                  autoComplete="off"
                  disabled={formDisabled}
                />
                {emailError.length ? <div className={style["error-text"]}>{emailError}</div> : ""}
              </div>
              <button className={style.btn} type="submit" disabled={formDisabled}>
                {formDisabled ? <Loading_ellipsis /> : dictionary["Reset password"]}
              </button>
            </form>
          </>
        ) : (
          <>
            <Link className={style.close} href="/sign-in">
              <CloseIcon />
            </Link>
            <h1 className={style.title} dangerouslySetInnerHTML={{ __html: `${dictionary["E-mail sent"]}` }} />
            <div className={style.content}>
              <div className={style["form-text"]}>
                <span dangerouslySetInnerHTML={{ __html: `${dictionary["E-mail sent"]}` }} />{" "}
                <b>{email}</b>.<br />
                {dictionary["Please click on the link provided in the message to set your new password."]}
              </div>
              <Link className={style.btn} href="/">
                {dictionary["Home"]}
              </Link>
            </div>
          </>
        )}
      </div>
      <MainBlob isFixed={true} />
    </Layout>
  );
}
